export const PROJECT_OPS_SCHEMA_VERSION = 1;

export const PROJECT_OPS_PROJECT_WORK_ITEM_TYPE = "project_ops.project";
export const PROJECT_OPS_ACTIVITY_RUN_WORK_ITEM_TYPE = "project_ops.activity_run";
export const PROJECT_OPS_TASK_WORK_ITEM_TYPE = "project_ops.task";

export const PROJECT_OPS_CUSTOM_HOME_PURCHASE_TYPE = "custom_home_purchase";
export const PROJECT_OPS_ENABLED_ORG_PREFIXES = ["a25"];
export const PROJECT_OPS_TEMPLATE_PREFERENCE_NAME = "projectOpsDefaultTemplate";
export const PROJECT_OPS_TEMPLATE_DRIVE_PATH = "private/_system/project-ops/templates/default-project-ops-template.json";

export const PROJECT_OPS_ORIGINATION_MODES = {
	internalFirst: "internal_first",
	customerFirst: "customer_first",
	funnelFirst: "funnel_first"
};

export const PROJECT_OPS_ACCOUNT_POLICIES = {
	inviteLater: "invite_later",
	requireAccountBeforeFirstActivity: "require_account_before_first_activity",
	inviteAfterQualifiedStage: "invite_after_qualified_stage"
};

export const PROJECT_OPS_CHANGE_MODES = {
	budgetSettlement: "budget_settlement",
	contractChange: "contract_change",
	hybrid: "hybrid"
};

export const PROJECT_OPS_BUDGET_APPLICATION_POLICIES = {
	explicit: "explicit",
	autoIfBudgetTarget: "auto_if_budget_target",
	autoIfSkuEligible: "auto_if_sku_eligible"
};

export const DEFAULT_CUSTOM_HOME_ACTIVITY_IDS = {
	structuralSelection: "8bc335b9-8269-42ba-ad6b-c0ad669f5874",
	plumbingSelection: "86ce108f-9eaa-4046-8518-49e62d5380bd",
	exteriorColorSelection: "cea4e799-373e-49f9-8f80-3e939b2c43ad",
	applianceSelection: "2e7f9a11-994a-462a-b618-ea3ac76de004"
};

export const PROJECT_OPS_STAGE_LABELS = {
	structural_selection: "Structural Selection",
	lock_initial_contract: "Lock Initial Contract",
	plumbing_selection: "Plumbing Selection"
};

export const CUSTOM_HOME_PROJECT_TEMPLATE = {
	key: "custom_home_purchase_phase_1",
	label: "Custom Home Purchase - Phase 1",
	projectType: PROJECT_OPS_CUSTOM_HOME_PURCHASE_TYPE,
	defaultOriginationMode: PROJECT_OPS_ORIGINATION_MODES.internalFirst,
	defaultAccountPolicy: PROJECT_OPS_ACCOUNT_POLICIES.inviteLater,
	stages: [
		{
			key: "structural_selection",
			label: PROJECT_OPS_STAGE_LABELS.structural_selection,
			workItemType: PROJECT_OPS_ACTIVITY_RUN_WORK_ITEM_TYPE,
			activityIdKey: "structuralSelection",
			activityIdStateField: "projectOpsStructuralActivityId",
			assigneeStateField: "projectOpsStructuralAssigneeUserSubId",
			dueAtStateField: "projectOpsStructuralDueAt",
			initialStatus: "open",
			priority: "normal",
			activityRole: "structural_selection",
			artifactType: "contract",
			contractMode: "initial_purchase",
			titleTemplate: "Structural Selection - {{projectName}}",
			description: "Capture initial customer information, structural selections, and starting budget lines.",
			activityName: "Structural Selection",
			projection: {
				reducerKey: "custom_home_structural_selection_v1",
				extractCustomerContact: true,
				extractPricingContract: true,
				extractBudgetFacts: true
			}
		},
		{
			key: "lock_initial_contract",
			label: PROJECT_OPS_STAGE_LABELS.lock_initial_contract,
			workItemType: PROJECT_OPS_TASK_WORK_ITEM_TYPE,
			assigneeStateField: "projectOpsContractLockAssigneeUserSubId",
			dueAtStateField: "projectOpsContractLockDueAt",
			initialStatus: "open",
			priority: "high",
			activityRole: "contract_lock",
			artifactType: "contract",
			contractMode: "lock_initial_purchase",
			contractDocumentId: "initial-purchase",
			titleTemplate: "Lock Initial Contract - {{projectName}}",
			description: "Review the structural selection invoice and lock the Initial Purchase agreement before addendums begin.",
			projection: {
				reducerKey: "project_contract_lock_v1",
				extractPricingContract: true
			}
		},
		{
			key: "plumbing_selection",
			label: PROJECT_OPS_STAGE_LABELS.plumbing_selection,
			workItemType: PROJECT_OPS_ACTIVITY_RUN_WORK_ITEM_TYPE,
			activityIdKey: "plumbingSelection",
				activityIdStateField: "projectOpsPlumbingActivityId",
				assigneeStateField: "projectOpsPlumbingAssigneeUserSubId",
				dueAtStateField: "projectOpsPlumbingDueAt",
				budgetTargetsStateField: "projectOpsPlumbingBudgetTargetsText",
				initialStatus: "blocked",
				priority: "normal",
				activityRole: "plumbing_selection",
				artifactType: "addendum",
				contractMode: "draft_addendum",
				changeMode: PROJECT_OPS_CHANGE_MODES.budgetSettlement,
				budgetApplicationPolicy: PROJECT_OPS_BUDGET_APPLICATION_POLICIES.autoIfBudgetTarget,
				addendumNumber: 1,
				defaultBudgetTargets: ["Plumbing Budget"],
			titleTemplate: "Addendum 1 - Plumbing Selection - {{projectName}}",
			description: "Run plumbing selections against the plumbing budget. Multiple budget targets can be listed for this addendum.",
			activityName: "Plumbing Selection",
			projection: {
				reducerKey: "custom_home_plumbing_addendum_v1",
				extractPricingContract: true,
				extractBudgetFacts: true
			}
		}
	]
};

function normalizeText(value) {
	return `${value || ""}`.trim();
}

export function isProjectOpsTemplateDocumentContent(content) {
	let template = content;
	try {
		if (typeof template === "string") {
			template = JSON.parse(template);
		}
	} catch (err) {
		return false;
	}

	if (!template || typeof template !== "object" || Array.isArray(template)) {
		return false;
	}

	const supportedWorkItemTypes = [
		PROJECT_OPS_PROJECT_WORK_ITEM_TYPE,
		PROJECT_OPS_ACTIVITY_RUN_WORK_ITEM_TYPE,
		PROJECT_OPS_TASK_WORK_ITEM_TYPE
	];
	const stages = template.stages;

	return Number(template.schemaVersion) === PROJECT_OPS_SCHEMA_VERSION
		&& Boolean(normalizeText(template.key))
		&& Boolean(normalizeText(template.label))
		&& Boolean(normalizeText(template.projectType))
		&& Object.values(PROJECT_OPS_ORIGINATION_MODES).indexOf(normalizeText(template.defaultOriginationMode)) !== -1
		&& Object.values(PROJECT_OPS_ACCOUNT_POLICIES).indexOf(normalizeText(template.defaultAccountPolicy)) !== -1
		&& Array.isArray(stages)
		&& stages.length > 0
		&& stages.every(stage => (
			stage
			&& typeof stage === "object"
			&& !Array.isArray(stage)
			&& Boolean(normalizeText(stage.key))
			&& Boolean(normalizeText(stage.label))
			&& supportedWorkItemTypes.indexOf(normalizeText(stage.workItemType)) !== -1
		));
}

export function isProjectOpsEnabledForOrg(orgId = "") {
	const normalizedOrgId = normalizeText(orgId).toLowerCase();
	return PROJECT_OPS_ENABLED_ORG_PREFIXES.some(prefix => normalizedOrgId.indexOf(prefix.toLowerCase()) === 0);
}

function slugify(value) {
	return normalizeText(value)
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
}

function compactObject(value) {
	return Object.keys(value || {}).reduce((acc, key) => {
		const entry = value[key];
		if (entry === undefined || entry === null || entry === "") {
			return acc;
		}
		if (Array.isArray(entry) && entry.length === 0) {
			return acc;
		}
		acc[key] = entry;
		return acc;
	}, {});
}

function clonePlainObject(value) {
	return JSON.parse(JSON.stringify(value || {}));
}

function normalizeArray(value) {
	if (Array.isArray(value)) {
		return value.filter(item => item !== undefined && item !== null);
	}
	return value === undefined || value === null || value === "" ? [] : [value];
}

function normalizeProjectOpsParticipant(participant = {}) {
	if (!participant || typeof participant !== "object") {
		return null;
	}
	const role = normalizeText(participant.role || participant.participantRole || "customer");
	const contact = participant.contact && typeof participant.contact === "object" ? participant.contact : participant;
	const userSubId = normalizeText(participant.userSubId || participant.subId);
	return compactObject({
		id: normalizeText(participant.id || participant.participantId || userSubId || contact.email || contact.phone || role),
		role,
		type: normalizeText(participant.type || (userSubId ? "user" : "contact")),
		status: normalizeText(participant.status || (userSubId ? "active" : "provisional")),
		userSubId,
		contact: compactObject({
			name: normalizeText(contact.name || contact.displayName),
			email: normalizeText(contact.email || contact.inviteEmail),
			phone: normalizeText(contact.phone || contact.phoneNumber)
		}),
		accountPolicy: normalizeText(participant.accountPolicy)
	});
}

export function normalizeProjectOpsParticipants(participants, fallbackCustomer = null) {
	const normalized = normalizeArray(participants)
		.map(normalizeProjectOpsParticipant)
		.filter(Boolean);
	const customer = fallbackCustomer && typeof fallbackCustomer === "object" ? fallbackCustomer : null;
	if (customer && (customer.name || customer.email || customer.phone) && !normalized.some(participant => participant.role === "customer")) {
		normalized.push(normalizeProjectOpsParticipant({
			role: "customer",
			type: "contact",
			status: "provisional",
			contact: customer
		}));
	}
	return normalized;
}

function normalizeProjectOpsArtifact(artifact = {}) {
	if (!artifact || typeof artifact !== "object") {
		return null;
	}
	return compactObject({
		type: normalizeText(artifact.type),
		role: normalizeText(artifact.role),
		stageKey: normalizeText(artifact.stageKey),
		activityId: normalizeText(artifact.activityId),
		activityInstanceId: normalizeText(artifact.activityInstanceId || artifact.instanceId),
		instanceUrl: normalizeText(artifact.instanceUrl || artifact.url),
		documentId: normalizeText(artifact.documentId),
		status: normalizeText(artifact.status),
		title: normalizeText(artifact.title || artifact.name),
		capturedAt: normalizeText(artifact.capturedAt || artifact.projectedAt)
	});
}

export function normalizeProjectOpsContractAnchor(anchor = null) {
	if (!anchor || typeof anchor !== "object") {
		return null;
	}
	return compactObject({
		projectId: normalizeText(anchor.projectId),
		projectName: normalizeText(anchor.projectName),
		workItemId: normalizeText(anchor.workItemId),
		activityId: normalizeText(anchor.activityId),
		activityInstanceId: normalizeText(anchor.activityInstanceId || anchor.instanceId),
		instanceUrl: normalizeText(anchor.instanceUrl || anchor.activityInstanceUrl || anchor.sharedSessionUrl || anchor.url),
		stageKey: normalizeText(anchor.stageKey),
		stageLabel: normalizeText(anchor.stageLabel),
		source: normalizeText(anchor.source || "project_ops_contract_anchor")
	});
}

export function normalizeProjectOpsArtifacts(artifacts) {
	return normalizeArray(artifacts).map(normalizeProjectOpsArtifact).filter(Boolean);
}

export function generateProjectOpsId(prefix = "project") {
	const random = Math.random().toString(36).slice(2, 8);
	return `${slugify(prefix) || "project"}-${Date.now().toString(36)}-${random}`;
}

export function normalizeProjectOpsBudgetTargets(targets) {
	const sourceTargets = Array.isArray(targets)
		? targets
		: (normalizeText(targets) ? normalizeText(targets).split(/[,\n;]/) : []);

	return sourceTargets
		.map((target, index) => {
			if (target && typeof target === "object") {
				const key = normalizeText(target.key || target.budgetKey || target.category || target.lineId || `budget-${index + 1}`);
				return compactObject({
					lineId: normalizeText(target.lineId || target.budgetLineId),
					key,
					label: normalizeText(target.label || target.name || target.description || key),
					category: normalizeText(target.category || target.budgetCategory || key),
					required: target.required === true
				});
			}
			const text = normalizeText(target);
			return text ? {
				key: slugify(text) || `budget-${index + 1}`,
				label: text,
				category: slugify(text) || text
			} : null;
		})
		.filter(Boolean);
}

function normalizeProjectOpsTemplateStage(stage = {}, fallbackStage = {}, index = 0) {
	const source = stage && typeof stage === "object" ? stage : {};
	const fallback = fallbackStage && typeof fallbackStage === "object" ? fallbackStage : {};
	const key = normalizeText(source.key || fallback.key || `stage_${index + 1}`);
	const activityIdKey = normalizeText(source.activityIdKey || fallback.activityIdKey);
	const projection = {
		...((fallback.projection && typeof fallback.projection === "object") ? fallback.projection : {}),
		...((source.projection && typeof source.projection === "object") ? source.projection : {})
	};
	const defaultBudgetTargets = normalizeProjectOpsBudgetTargets(
		Object.prototype.hasOwnProperty.call(source, "defaultBudgetTargets")
			? source.defaultBudgetTargets
			: fallback.defaultBudgetTargets
	);
	const addendumNumber = source.addendumNumber === undefined || source.addendumNumber === null || source.addendumNumber === ""
		? fallback.addendumNumber
		: Number(source.addendumNumber);

	return compactObject({
		...clonePlainObject(fallback),
		...clonePlainObject(source),
		key,
		label: normalizeText(source.label || fallback.label || PROJECT_OPS_STAGE_LABELS[key] || key),
		workItemType: normalizeText(source.workItemType || fallback.workItemType || PROJECT_OPS_TASK_WORK_ITEM_TYPE),
		activityIdKey,
		activityIdStateField: normalizeText(source.activityIdStateField || fallback.activityIdStateField),
		defaultActivityId: normalizeText(source.defaultActivityId || fallback.defaultActivityId || (activityIdKey ? DEFAULT_CUSTOM_HOME_ACTIVITY_IDS[activityIdKey] : "")),
		assigneeStateField: normalizeText(source.assigneeStateField || fallback.assigneeStateField),
		dueAtStateField: normalizeText(source.dueAtStateField || fallback.dueAtStateField),
		budgetTargetsStateField: normalizeText(source.budgetTargetsStateField || fallback.budgetTargetsStateField),
		initialStatus: normalizeText(source.initialStatus || fallback.initialStatus || "open"),
		priority: normalizeText(source.priority || fallback.priority || "normal"),
		activityRole: normalizeText(source.activityRole || fallback.activityRole || key),
		artifactType: normalizeText(source.artifactType || fallback.artifactType),
		contractMode: normalizeText(source.contractMode || fallback.contractMode),
		changeMode: normalizeText(source.changeMode || fallback.changeMode),
		budgetApplicationPolicy: normalizeText(source.budgetApplicationPolicy || fallback.budgetApplicationPolicy),
		contractDocumentId: normalizeText(source.contractDocumentId || fallback.contractDocumentId),
		addendumNumber: Number.isFinite(addendumNumber) ? addendumNumber : undefined,
		defaultBudgetTargets,
		titleTemplate: normalizeText(source.titleTemplate || fallback.titleTemplate || `${source.label || fallback.label || key} - {{projectName}}`),
		description: normalizeText(source.description || fallback.description),
		activityName: normalizeText(source.activityName || fallback.activityName || source.label || fallback.label),
		projection: Object.keys(projection).length > 0 ? projection : undefined
	});
}

export function normalizeProjectOpsTemplate(template = {}, fallbackTemplate = CUSTOM_HOME_PROJECT_TEMPLATE) {
	const fallback = fallbackTemplate && typeof fallbackTemplate === "object" ? fallbackTemplate : {};
	const source = template && typeof template === "object" ? template : {};
	const fallbackStages = Array.isArray(fallback.stages) ? fallback.stages : [];
	const sourceStages = Array.isArray(source.stages) && source.stages.length > 0 ? source.stages : fallbackStages;
	const key = normalizeText(source.key || fallback.key || "project_ops_template");
	const stages = sourceStages.map((stage, index) => normalizeProjectOpsTemplateStage(stage, fallbackStages[index], index));

	return compactObject({
		...clonePlainObject(fallback),
		...clonePlainObject(source),
		schemaVersion: Number(source.schemaVersion || fallback.schemaVersion || PROJECT_OPS_SCHEMA_VERSION),
		key,
		label: normalizeText(source.label || fallback.label || key),
		description: normalizeText(source.description || fallback.description),
		projectType: normalizeText(source.projectType || fallback.projectType || key),
		defaultOriginationMode: normalizeText(source.defaultOriginationMode || fallback.defaultOriginationMode || PROJECT_OPS_ORIGINATION_MODES.internalFirst),
		defaultAccountPolicy: normalizeText(source.defaultAccountPolicy || fallback.defaultAccountPolicy || PROJECT_OPS_ACCOUNT_POLICIES.inviteLater),
		stages
	});
}

export function getProjectOpsTemplateBudgetTargetsText(stage = {}) {
	return normalizeProjectOpsBudgetTargets(stage.defaultBudgetTargets || [])
		.map(target => target.label || target.key || target.lineId)
		.filter(Boolean)
		.join("\n");
}

export function getProjectOpsTemplateBuilderStageDefaults(template = CUSTOM_HOME_PROJECT_TEMPLATE) {
	const defaults = {};
	const normalizedTemplate = normalizeProjectOpsTemplate(template);
	(normalizedTemplate.stages || []).forEach(stage => {
		if (stage.activityIdStateField) {
			defaults[stage.activityIdStateField] = stage.defaultActivityId || (stage.activityIdKey ? DEFAULT_CUSTOM_HOME_ACTIVITY_IDS[stage.activityIdKey] || "" : "");
		}
		if (stage.budgetTargetsStateField) {
			defaults[stage.budgetTargetsStateField] = getProjectOpsTemplateBudgetTargetsText(stage);
		}
		if (stage.assigneeStateField) {
			defaults[stage.assigneeStateField] = "";
		}
		if (stage.dueAtStateField) {
			defaults[stage.dueAtStateField] = "";
		}
	});
	return defaults;
}

function getBudgetCandidateText(invoiceItem = {}) {
	return [
		invoiceItem.lineId,
		invoiceItem.description,
		invoiceItem.productId,
		invoiceItem.allowance && invoiceItem.allowance.category,
		invoiceItem.allowance && invoiceItem.allowance.key,
		invoiceItem.allowance && invoiceItem.allowance.label
	].map(normalizeText).filter(Boolean);
}

export function invoiceItemMatchesProjectOpsBudgetTarget(invoiceItem = {}, budgetTarget = {}) {
	if (!invoiceItem || invoiceItem.lineType !== "allowance" || !budgetTarget) {
		return false;
	}

	const targetLineId = normalizeText(budgetTarget.lineId);
	if (targetLineId && normalizeText(invoiceItem.lineId) === targetLineId) {
		return true;
	}

	const targetValues = [
		budgetTarget.key,
		budgetTarget.label,
		budgetTarget.category
	].map(normalizeText).filter(Boolean);

	if (targetValues.length === 0) {
		return false;
	}

	const candidateText = getBudgetCandidateText(invoiceItem);
	const candidateSlugs = candidateText.map(slugify);

	return targetValues.some((targetValue) => {
		const targetSlug = slugify(targetValue);
		return candidateText.some((candidate) => candidate.toLowerCase() === targetValue.toLowerCase())
			|| candidateSlugs.indexOf(targetSlug) !== -1
			|| candidateText.some((candidate) => slugify(candidate).indexOf(targetSlug) !== -1);
	});
}

function normalizeBudgetEligibilityValues(value) {
	if (Array.isArray(value)) {
		return value.flatMap(normalizeBudgetEligibilityValues);
	}
	if (value && typeof value === "object") {
		return [
			value.key,
			value.label,
			value.category,
			value.name,
			value.value,
			value.id
		].map(normalizeText).filter(Boolean);
	}
	const text = normalizeText(value);
	return text ? text.split(/[,\n;|]/).map(normalizeText).filter(Boolean) : [];
}

function getProjectOpsSkuBudgetEligibilityValues(source = {}) {
	if (!source || typeof source !== "object") {
		return [];
	}

	const metadata = source.metadata && typeof source.metadata === "object" ? source.metadata : {};
	const projectOps = source.projectOps && typeof source.projectOps === "object" ? source.projectOps : {};
	const candidates = [
		source.budgetTargets,
		source.budgetTarget,
		source.budgetCategories,
		source.budgetCategory,
		source.allowanceCategories,
		source.allowanceCategory,
		source.projectOpsBudgetTargets,
		source.projectOpsBudgetCategories,
		projectOps.budgetTargets,
		projectOps.budgetTarget,
		projectOps.budgetCategories,
		projectOps.budgetCategory,
		metadata.budgetTargets,
		metadata.budgetTarget,
		metadata.budgetCategories,
		metadata.budgetCategory,
		metadata.allowanceCategories,
		metadata.allowanceCategory,
		source.tags,
		source.categories,
		source.category,
		source.productCategory,
		source.itemCategory,
		source.department,
		source.type,
		source.group,
		source.groups
	];

	return Array.from(new Set(candidates.flatMap(normalizeBudgetEligibilityValues).map(slugify).filter(Boolean)));
}

function getProjectOpsBooleanFlag(source = {}, keys = []) {
	if (!source || typeof source !== "object") {
		return null;
	}

	const metadata = source.metadata && typeof source.metadata === "object" ? source.metadata : {};
	const projectOps = source.projectOps && typeof source.projectOps === "object" ? source.projectOps : {};
	const candidates = [source, metadata, projectOps];
	for (let candidateIndex = 0; candidateIndex < candidates.length; candidateIndex += 1) {
		const candidate = candidates[candidateIndex];
		for (let keyIndex = 0; keyIndex < keys.length; keyIndex += 1) {
			const key = keys[keyIndex];
			if (Object.prototype.hasOwnProperty.call(candidate, key)) {
				const value = candidate[key];
				if (value === true || value === "true" || value === "yes" || value === 1 || value === "1") {
					return true;
				}
				if (value === false || value === "false" || value === "no" || value === 0 || value === "0") {
					return false;
				}
			}
		}
	}
	return null;
}

export function projectOpsSkuMatchesBudgetTarget(source = {}, budgetTarget = {}) {
	const eligibilityValues = getProjectOpsSkuBudgetEligibilityValues(source);
	if (eligibilityValues.length === 0) {
		return false;
	}

	const targetValues = [
		budgetTarget.key,
		budgetTarget.label,
		budgetTarget.category
	].map(slugify).filter(Boolean);
	return targetValues.some(targetValue => (
		eligibilityValues.indexOf(targetValue) !== -1
		|| eligibilityValues.some(value => value.indexOf(targetValue) !== -1 || targetValue.indexOf(value) !== -1)
	));
}

export function shouldApplyProjectOpsBudgetSettlement({ projectOps = {}, invoiceItem = {}, budgetTarget = null } = {}) {
	const normalizedProjectOps = normalizeProjectOpsContext(projectOps);
	if (!budgetTarget || !invoiceItem || invoiceItem.lineType === "allowance" || invoiceItem.recordType === "allowance") {
		return false;
	}

	const eligibleFlag = getProjectOpsBooleanFlag(invoiceItem, ["budgetEligible", "allowanceEligible", "projectOpsBudgetEligible"]);
	if (eligibleFlag === false) {
		return false;
	}

	const changeMode = normalizeText(normalizedProjectOps.changeMode || PROJECT_OPS_CHANGE_MODES.contractChange).toLowerCase();
	const policy = normalizeText(normalizedProjectOps.budgetApplicationPolicy).toLowerCase()
		|| (changeMode === PROJECT_OPS_CHANGE_MODES.budgetSettlement
			? PROJECT_OPS_BUDGET_APPLICATION_POLICIES.autoIfBudgetTarget
			: PROJECT_OPS_BUDGET_APPLICATION_POLICIES.explicit);

	if (policy === PROJECT_OPS_BUDGET_APPLICATION_POLICIES.explicit || changeMode === PROJECT_OPS_CHANGE_MODES.contractChange) {
		return false;
	}

	if (eligibleFlag === true || projectOpsSkuMatchesBudgetTarget(invoiceItem, budgetTarget)) {
		return true;
	}

	return changeMode === PROJECT_OPS_CHANGE_MODES.budgetSettlement
		&& policy === PROJECT_OPS_BUDGET_APPLICATION_POLICIES.autoIfBudgetTarget;
}

export function normalizeProjectOpsContext(projectOps = {}) {
	const source = projectOps && typeof projectOps === "object" ? projectOps : {};
	const budgetTargets = normalizeProjectOpsBudgetTargets(source.budgetTargets || source.budgetTarget || []);
	const firstBudgetTarget = budgetTargets[0] || {};
	const projectId = normalizeText(source.projectId || source.id || source.projectOpsId);
	const stageKey = normalizeText(source.stageKey || source.currentStageKey || (source.stage && source.stage.key));
	const activityRole = normalizeText(source.activityRole || source.role || (source.activity && source.activity.role));
	const projectContractAnchor = normalizeProjectOpsContractAnchor(source.projectContractAnchor || source.contractAnchor);
	const addendumNumber = source.addendumNumber === undefined || source.addendumNumber === null || source.addendumNumber === ""
		? null
		: Number(source.addendumNumber);

	return compactObject({
		...source,
		schemaVersion: PROJECT_OPS_SCHEMA_VERSION,
		projectId,
		projectType: normalizeText(source.projectType || source.type),
		projectName: normalizeText(source.projectName || source.name || source.title),
		projectStatus: normalizeText(source.projectStatus || source.status || "open"),
			stageKey,
			stageLabel: normalizeText(source.stageLabel || (source.stage && source.stage.label) || PROJECT_OPS_STAGE_LABELS[stageKey] || stageKey),
			stepKey: normalizeText(source.stepKey || source.currentStepKey),
			activityRole,
			artifactType: normalizeText(source.artifactType || source.documentType),
			contractMode: normalizeText(source.contractMode || source.mode),
			changeMode: normalizeText(source.changeMode || source.changeType || source.projectOpsChangeMode || PROJECT_OPS_CHANGE_MODES.contractChange),
			budgetApplicationPolicy: normalizeText(source.budgetApplicationPolicy || source.budgetPolicy || source.projectOpsBudgetApplicationPolicy || ""),
			contractDocumentId: normalizeText(source.contractDocumentId || source.documentId),
		addendumNumber: addendumNumber || null,
		budgetTargetMode: normalizeText(source.budgetTargetMode || (budgetTargets.length > 1 ? "multi" : budgetTargets.length === 1 ? "single" : "")),
		activeBudgetLineId: normalizeText(source.activeBudgetLineId || source.budgetTargetLineId || firstBudgetTarget.lineId),
		budgetTargets,
		customer: source.customer && typeof source.customer === "object" ? compactObject({
			name: normalizeText(source.customer.name),
			email: normalizeText(source.customer.email),
			phone: normalizeText(source.customer.phone)
		}) : undefined,
		participants: normalizeProjectOpsParticipants(source.participants, source.customer),
		facts: source.facts && typeof source.facts === "object" ? source.facts : undefined,
		artifacts: normalizeProjectOpsArtifacts(source.artifacts),
		projectContractAnchor,
		origination: source.origination && typeof source.origination === "object" ? compactObject({
			mode: normalizeText(source.origination.mode),
			accountPolicy: normalizeText(source.origination.accountPolicy),
			source: normalizeText(source.origination.source),
			createdFromStageKey: normalizeText(source.origination.createdFromStageKey)
		}) : undefined,
		projection: source.projection && typeof source.projection === "object" ? source.projection : undefined,
		source: source.source && typeof source.source === "object" ? source.source : undefined
	});
}

export function buildProjectOpsActivityVars(projectOps = {}) {
	const normalized = normalizeProjectOpsContext(projectOps);
	const budgetTargets = normalized.budgetTargets || [];
	const budgetLineIds = budgetTargets.map((target) => target.lineId).filter(Boolean);
	const budgetKeys = budgetTargets.map((target) => target.key).filter(Boolean);
	const budgetLabels = budgetTargets.map((target) => target.label).filter(Boolean);
	const projectContractAnchor = normalized.projectContractAnchor || {};

	return compactObject({
		projectOps: normalized,
		projectOpsProjectId: normalized.projectId,
		projectOpsProjectName: normalized.projectName,
		projectOpsProjectType: normalized.projectType,
		projectOpsProjectStatus: normalized.projectStatus,
		projectOpsStageKey: normalized.stageKey,
			projectOpsStageLabel: normalized.stageLabel,
			projectOpsStepKey: normalized.stepKey,
			projectOpsActivityRole: normalized.activityRole,
			projectOpsArtifactType: normalized.artifactType,
			projectOpsContractMode: normalized.contractMode,
			projectOpsChangeMode: normalized.changeMode,
			projectOpsBudgetApplicationPolicy: normalized.budgetApplicationPolicy,
			projectOpsContractDocumentId: normalized.contractDocumentId,
		projectOpsAddendumNumber: normalized.addendumNumber,
		projectOpsBudgetTargetMode: normalized.budgetTargetMode,
		projectOpsBudgetTargetLineId: normalized.activeBudgetLineId,
		projectOpsBudgetTargetLineIds: budgetLineIds,
		projectOpsBudgetTargetKeys: budgetKeys,
		projectOpsBudgetTargetLabels: budgetLabels,
		projectOpsContractAnchorWorkItemId: projectContractAnchor.workItemId,
		projectOpsContractAnchorActivityId: projectContractAnchor.activityId,
		projectOpsContractAnchorActivityInstanceId: projectContractAnchor.activityInstanceId,
		projectOpsContractAnchorInstanceUrl: projectContractAnchor.instanceUrl,
		projectOpsContractAnchorStageKey: projectContractAnchor.stageKey,
		projectName: normalized.projectName,
		customerName: normalized.customer && normalized.customer.name,
		customerEmail: normalized.customer && normalized.customer.email,
		customerPhone: normalized.customer && normalized.customer.phone
	});
}

export function buildProjectOpsLaunchContext(projectOps = {}, extraActivityVars = {}, extraContext = {}) {
	const normalized = normalizeProjectOpsContext(projectOps);
	const activityVars = {
		...buildProjectOpsActivityVars(normalized),
		...(extraActivityVars && typeof extraActivityVars === "object" ? extraActivityVars : {})
	};
	return compactObject({
		...extraContext,
		projectOps: normalized,
		activityVars
	});
}

export function getProjectOpsFromWorkItem(workItem = {}) {
	const metadata = workItem && typeof workItem.metadata === "object" ? workItem.metadata : {};
	const progress = workItem && typeof workItem.progress === "object"
		? workItem.progress
		: (metadata.progress && typeof metadata.progress === "object" ? metadata.progress : {});
	return normalizeProjectOpsContext(
		workItem.projectOps
		|| progress.projectOps
		|| (progress.activityOps && progress.activityOps.projectOps)
		|| metadata.projectOps
		|| (metadata.launchContext && metadata.launchContext.projectOps)
		|| (metadata.progress && metadata.progress.projectOps)
		|| (metadata.progress && metadata.progress.activityOps && metadata.progress.activityOps.projectOps)
		|| {}
	);
}

export function isProjectOpsWorkItem(workItem = {}) {
	return [PROJECT_OPS_PROJECT_WORK_ITEM_TYPE, PROJECT_OPS_ACTIVITY_RUN_WORK_ITEM_TYPE, PROJECT_OPS_TASK_WORK_ITEM_TYPE].indexOf(workItem.type) !== -1
		|| Boolean(getProjectOpsFromWorkItem(workItem).projectId);
}

export function renderProjectOpsTemplate(value = "", context = {}) {
	return `${value || ""}`.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (match, key) => {
		const normalizedKey = normalizeText(key);
		return Object.prototype.hasOwnProperty.call(context, normalizedKey) ? `${context[normalizedKey] || ""}` : match;
	});
}

export function getCustomHomeProjectTemplate() {
	return normalizeProjectOpsTemplate(CUSTOM_HOME_PROJECT_TEMPLATE);
}

export function buildProjectOpsParticipant({
	role = "customer",
	name = "",
	email = "",
	phone = "",
	userSubId = "",
	status = "",
	accountPolicy = ""
} = {}) {
	return normalizeProjectOpsParticipant({
		role,
		type: userSubId ? "user" : "contact",
		status: status || (userSubId ? "active" : "provisional"),
		userSubId,
		accountPolicy,
		contact: {
			name,
			email,
			phone
		}
	});
}

export function buildProjectOpsProjectContext({
	template = CUSTOM_HOME_PROJECT_TEMPLATE,
	projectId,
	projectName,
	customerName,
	customerEmail,
	customerPhone,
	originationMode,
	accountPolicy
} = {}) {
	const normalizedTemplate = normalizeProjectOpsTemplate(template);
	const firstStage = (normalizedTemplate.stages || [])[0] || {};
	const resolvedOriginationMode = normalizeText(originationMode || normalizedTemplate.defaultOriginationMode || PROJECT_OPS_ORIGINATION_MODES.internalFirst);
	const resolvedAccountPolicy = normalizeText(accountPolicy || normalizedTemplate.defaultAccountPolicy || PROJECT_OPS_ACCOUNT_POLICIES.inviteLater);
	const resolvedProjectId = normalizeText(projectId) || generateProjectOpsId(projectName || customerName || normalizedTemplate.projectType || "project");
	const resolvedProjectName = normalizeText(projectName) || normalizeText(customerName) || normalizedTemplate.label || "Project";
	const customer = compactObject({
		name: normalizeText(customerName),
		email: normalizeText(customerEmail),
		phone: normalizeText(customerPhone)
	});
	const customerParticipant = buildProjectOpsParticipant({
		role: "customer",
		name: customer.name,
		email: customer.email,
		phone: customer.phone,
		status: "provisional",
		accountPolicy: resolvedAccountPolicy
	});
	return normalizeProjectOpsContext({
		projectId: resolvedProjectId,
		projectType: normalizedTemplate.projectType,
		projectName: resolvedProjectName,
		projectStatus: "open",
		stageKey: firstStage.key,
		stageLabel: firstStage.label,
		origination: {
			mode: resolvedOriginationMode,
			accountPolicy: resolvedAccountPolicy,
			source: "activityOperationsProjectOps"
		},
		facts: {
			customer,
			originationMode: resolvedOriginationMode,
			accountPolicy: resolvedAccountPolicy
		},
		participants: customerParticipant ? [customerParticipant] : [],
		source: {
			templateKey: normalizedTemplate.key,
			templateLabel: normalizedTemplate.label
		},
		customer
	});
}

export function buildCustomHomeProjectContext(options = {}) {
	return buildProjectOpsProjectContext({
		...options,
		template: CUSTOM_HOME_PROJECT_TEMPLATE
	});
}

export function buildProjectOpsStageContext(projectOps = {}, stage = {}, overrides = {}) {
	return buildCustomHomeActivityRunContext(projectOps, {
			stageKey: stage.key,
			stageLabel: stage.label,
			stepKey: stage.key,
			activityRole: stage.activityRole,
			artifactType: stage.artifactType,
			contractMode: stage.contractMode,
			changeMode: stage.changeMode,
			budgetApplicationPolicy: stage.budgetApplicationPolicy,
			contractDocumentId: stage.contractDocumentId,
			addendumNumber: stage.addendumNumber,
			projection: stage.projection,
			...overrides
		});
	}

export function buildCustomHomeActivityRunContext(projectOps = {}, overrides = {}) {
	return normalizeProjectOpsContext({
		...projectOps,
		...overrides,
		customer: {
			...(projectOps.customer || {}),
			...(overrides.customer || {})
		},
		source: {
			...(projectOps.source || {}),
			...(overrides.source || {})
		}
	});
}

export function summarizeProjectOpsContext(projectOps = {}) {
	const normalized = normalizeProjectOpsContext(projectOps);
	return [
		normalized.projectName,
		normalized.stageLabel,
		normalized.addendumNumber ? `Addendum ${normalized.addendumNumber}` : "",
		(normalized.budgetTargets || []).map((target) => target.label || target.key).filter(Boolean).join(", ")
	].filter(Boolean).join(" - ");
}
